'use client';

import { c as _c } from "react/compiler-runtime";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { getCollectionBySlug } from '@src/actions/content.actions';
import { Button } from '@src/components/Button/Button';
import { ProgramsRail } from '@src/components/ProgramsRail/ProgramsRail';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { parseCollectionModuleToProgramsRailData } from '@src/mappers/collection/rails-fetch.mapper';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import { trackDismissPostWorkout, trackSelectLinkItem, trackViewPostWorkout } from '@src/utils/post-workout-analytics.util';
import { getCocoaCollectionQueryKey } from '@src/utils/query.util';
import { PostWorkoutInterstitial } from './PostWorkoutInterstitial';
import { PostWorkoutWorkoutCard } from './PostWorkoutWorkoutCard';
import { instrumentAction } from "@src/utils/instrumentation";
export interface IPostWorkoutExperienceProps {
  isOpen: boolean;
  handleClose: () => void;
  entityData?: CocoApi.PlayableEntity;
  isQVThresholdReached: boolean;
  percentProgress?: number;
}
const FIVER_COLLECTION_SLUG = 'pwe-5er-web';
const EXPLORE_COLLECTION_SLUG = 'pwe-explore-web';
const EXPLORE_CODE = 'WEB_PWE';
const INTERSTITIAL_DURATION_MS = 3500;
const INTERSTITIAL_FADE_OUT_MS = 500;
const MAX_DESKTOP_ITEMS = 3;
function appendCodeParam(href: string | undefined, code?: string) {
  if (!href || !code) return href ?? '#';
  const url = new URL(href, window.location.origin);
  url.searchParams.set('code', code);
  return url.toString();
}
const PostWorkoutExperience: React.FC<IPostWorkoutExperienceProps> = t0 => {
  const $ = _c(93);
  const {
    entityData,
    isOpen,
    handleClose,
    isQVThresholdReached,
    percentProgress
  } = t0;
  const {
    t,
    locale
  } = useLocale("postWorkout");
  const {
    user
  } = useSession();
  const {
    isDesktop
  } = useBreakpoints();
  const isUSorCanada = locale.countryCode === "US" || locale.countryCode === "CA";
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [interstitialHiding, setInterstitialHiding] = useState(false);
  const hideTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const viewTrackedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      if (!isOpen) {
        return;
      }
      setShowInterstitial(true);
      setInterstitialHiding(false);
      fadeTimerRef.current = setTimeout(() => {
        setInterstitialHiding(true);
      }, INTERSTITIAL_DURATION_MS - INTERSTITIAL_FADE_OUT_MS) as unknown as number;
      hideTimerRef.current = setTimeout(() => {
        setShowInterstitial(false);
      }, INTERSTITIAL_DURATION_MS) as unknown as number;
      return () => {
        if (fadeTimerRef.current !== null) {
          clearTimeout(fadeTimerRef.current);
        }
        if (hideTimerRef.current !== null) {
          clearTimeout(hideTimerRef.current);
        }
      };
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== entityData?.primaryParent?.slug || $[4] !== entityData?.slug || $[5] !== entityData?.video?.videoId || $[6] !== isOpen || $[7] !== percentProgress) {
    t3 = () => {
      if (!isOpen) {
        viewTrackedRef.current = false;
        return;
      }
      const videoId = entityData?.video?.videoId;
      if (viewTrackedRef.current || !videoId) {
        return;
      }
      viewTrackedRef.current = true;
      trackViewPostWorkout({
        videoId,
        videoSlug: entityData?.slug,
        videoParentSlug: entityData?.primaryParent?.slug,
        percentProgress
      });
    };
    $[3] = entityData?.primaryParent?.slug;
    $[4] = entityData?.slug;
    $[5] = entityData?.video?.videoId;
    $[6] = isOpen;
    $[7] = percentProgress;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = entityData?.video?.videoId;
  const t5 = entityData?.slug;
  const t6 = entityData?.primaryParent?.slug;
  let t7;
  if ($[9] !== isOpen || $[10] !== percentProgress || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = [isOpen, t4, t5, t6, percentProgress];
    $[9] = isOpen;
    $[10] = percentProgress;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t3, t7);
  let t8;
  if ($[15] !== locale.code) {
    t8 = getCocoaCollectionQueryKey(FIVER_COLLECTION_SLUG, locale.code);
    $[15] = locale.code;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== locale.code) {
    t9 = () => instrumentAction("ContentActions.getCollectionBySlug", getCollectionBySlug)({
      slug: FIVER_COLLECTION_SLUG,
      locale: locale.code
    });
    $[17] = locale.code;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== isQVThresholdReached || $[20] !== t8 || $[21] !== t9) {
    t10 = {
      queryKey: t8,
      queryFn: t9,
      enabled: isQVThresholdReached
    };
    $[19] = isQVThresholdReached;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const {
    data: pwe5erCollectionData
  } = useQuery(t10);
  let t11;
  if ($[23] !== locale.code) {
    t11 = getCocoaCollectionQueryKey(EXPLORE_COLLECTION_SLUG, locale.code);
    $[23] = locale.code;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== locale.code) {
    t12 = () => instrumentAction("ContentActions.getCollectionBySlug", getCollectionBySlug)({
      slug: EXPLORE_COLLECTION_SLUG,
      locale: locale.code
    });
    $[25] = locale.code;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = isQVThresholdReached && isUSorCanada;
  let t14;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t13) {
    t14 = {
      queryKey: t11,
      queryFn: t12,
      enabled: t13
    };
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const {
    data: pweExploreCollectionData
  } = useQuery(t14);
  let t15;
  if ($[31] !== handleClose) {
    t15 = () => {
      trackDismissPostWorkout();
      handleClose();
    };
    $[31] = handleClose;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const handleDismiss = t15;
  let t16;
  if ($[33] !== isDesktop) {
    t16 = (collection, collectionSlug, code) => {
      const railData = parseCollectionModuleToProgramsRailData(collection, "collection");
      if (!railData?.items?.length) {
        return null;
      }
      const {
        title,
        subtitle,
        items
      } = railData;
      const itemsWithCode = code ? items.map(item => ({
        ...item,
        redirectUrl: appendCodeParam(item.redirectUrl, code)
      })) : items;
      return <ProgramsRail className="[&_h2]:text-heading-5 [&_h2]:font-medium [&_p]:text-body-sm [&_p]:text-bodi-gray-butters" columns={4} compact={true} items={isDesktop ? itemsWithCode.slice(0, MAX_DESKTOP_ITEMS) : itemsWithCode} subtitle={subtitle} title={title} onItemSelect={(item_0, index) => trackSelectLinkItem({
        linkSlug: item_0.slug,
        index,
        collectionSlug
      })} />;
    };
    $[33] = isDesktop;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const renderCollection = t16;
  if (!isOpen) {
    return null;
  }
  let t17;
  if ($[35] !== handleClose || $[36] !== interstitialHiding || $[37] !== showInterstitial || $[38] !== t) {
    t17 = showInterstitial && <PostWorkoutInterstitial key="post-workout-interstitial" headline={t("thatsHowItsDone")} isHiding={interstitialHiding} onClose={handleClose} />;
    $[35] = handleClose;
    $[36] = interstitialHiding;
    $[37] = showInterstitial;
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <AnimatePresence>{t17}</AnimatePresence>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <XMarkIcon className="size-6 stroke-[2px] text-bodi-white" />;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== handleClose) {
    t20 = <button aria-label="Close" className="fixed top-5 right-14 z-100002 hidden size-10 items-center justify-center rounded-full bg-white/5 lg:flex" onClick={handleClose} type="button">{t19}</button>;
    $[43] = handleClose;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <XMarkIcon className="size-6 stroke-[2px] text-bodi-white" />;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== handleClose) {
    t22 = <div className="flex w-full justify-end lg:hidden"><button aria-label="Close" className="flex size-10 items-center justify-center rounded-full bg-white/5" onClick={handleClose} type="button">{t21}</button></div>;
    $[46] = handleClose;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="pointer-events-none absolute top-1/2 right-[calc(50%-50vw)] left-[calc(100%-556px-4rem)] z-0 hidden h-screen -translate-y-1/2 bg-white/3 lg:block" />;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const t24 = user?.firstName;
  let t25;
  if ($[49] !== t || $[50] !== t24) {
    t25 = t("greatJobToday", {
      name: t24
    });
    $[49] = t;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t25) {
    t26 = <p className="text-heading-3 m-0 font-lateral font-bold text-bodi-white">{t25}</p>;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const t27 = entityData as CocoApi.WorkoutEntity;
  let t28;
  if ($[54] !== t27) {
    t28 = <PostWorkoutWorkoutCard entityData={t27} />;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t) {
    t29 = t("doneForToday");
    $[56] = t;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== handleDismiss || $[59] !== t29) {
    t30 = <Button className="hidden self-start lg:flex lg:h-[60px] lg:w-[170px]" onClick={handleDismiss} size="medium" type="button" variant="primary">{t29}</Button>;
    $[58] = handleDismiss;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t26 || $[62] !== t28 || $[63] !== t30) {
    t31 = <div className="flex w-full min-w-0 flex-col gap-4 lg:flex-1 lg:gap-8">{t26}{t28}{t30}</div>;
    $[61] = t26;
    $[62] = t28;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== pwe5erCollectionData || $[66] !== renderCollection) {
    t32 = pwe5erCollectionData && renderCollection(pwe5erCollectionData, FIVER_COLLECTION_SLUG);
    $[65] = pwe5erCollectionData;
    $[66] = renderCollection;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== isUSorCanada || $[69] !== pweExploreCollectionData || $[70] !== renderCollection) {
    t33 = isUSorCanada && pweExploreCollectionData && renderCollection(pweExploreCollectionData, EXPLORE_COLLECTION_SLUG, EXPLORE_CODE);
    $[68] = isUSorCanada;
    $[69] = pweExploreCollectionData;
    $[70] = renderCollection;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t32 || $[73] !== t33) {
    t34 = <div className="relative z-10 flex min-w-0 flex-col gap-6 lg:w-[504px] lg:shrink-0 lg:gap-8">{t32}{t33}</div>;
    $[72] = t32;
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t31 || $[76] !== t34) {
    t35 = <div className="relative z-10 mx-auto flex w-full max-w-[1408px] min-w-0 flex-col gap-6 lg:flex-row lg:gap-32 lg:px-14">{t23}{t31}{t34}</div>;
    $[75] = t31;
    $[76] = t34;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t22 || $[79] !== t35) {
    t36 = <div className="flex flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain px-6 pt-5 pb-[120px] lg:contents">{t22}{t35}</div>;
    $[78] = t22;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== t) {
    t37 = t("doneForToday");
    $[81] = t;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== handleDismiss || $[84] !== t37) {
    t38 = <footer className="pointer-events-none fixed inset-x-0 bottom-0 z-100002 h-[100px] bg-linear-to-t from-bodi-black-text/80 from-70% to-transparent lg:hidden"><div className="absolute inset-x-4 top-7"><Button className="pointer-events-auto h-auto w-full py-[13px] lg:w-[345px]" onClick={handleDismiss} size="medium" type="button" variant="primary">{t37}</Button></div></footer>;
    $[83] = handleDismiss;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== t20 || $[87] !== t36 || $[88] !== t38) {
    t39 = <div className="fixed inset-0 z-100001 flex flex-col overflow-clip bg-bodi-black-text lg:items-center lg:justify-center lg:overflow-y-auto">{t20}{t36}{t38}</div>;
    $[86] = t20;
    $[87] = t36;
    $[88] = t38;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== t18 || $[91] !== t39) {
    t40 = <>{t18}{t39}</>;
    $[90] = t18;
    $[91] = t39;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  return t40;
};
export default PostWorkoutExperience;